'use client';

import type { Currency } from '@/api/types/public';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { useMemo } from 'react';
import type { BestsellerPriceInfo, BestsellerProduct } from './types';

interface BestsellerDisplayPrice {
  price: string;
  originPrice?: string;
}

export type BestsellerDisplaySurface = 'pc' | 'wap';

function isEuroDisplayCurrency(currency: Currency): boolean {
  if (!currency) return false;
  if (currency.name === 'EUR') return true;

  return [currency.full_symbol, currency.symbol, currency.symbol_prefix, currency.symbol_suffix]
    .filter(Boolean)
    .some((value) => String(value).includes('€'));
}

export function resolveBestsellerDisplayCurrency(currency: Currency): Currency {
  if (!isEuroDisplayCurrency(currency)) return currency;
  if (currency.symbol_prefix === '' && currency.symbol_suffix === ' €') return currency;

  return {
    ...currency,
    symbol_prefix: '',
    symbol_suffix: ' €',
  };
}

function priceText(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' ? value.trim() : '';
}

function numberText(value: unknown): string {
  const text = priceText(value).replace(/,/g, '');
  if (!/^\d+(?:\.\d+)?$/.test(text)) return '';
  return text;
}

function getUserMemberLevel(userInfo: unknown): number | undefined {
  if (!userInfo || typeof userInfo !== 'object') return undefined;
  const user = userInfo as { is_member?: unknown; member_level?: unknown };
  if (Number(user.is_member) !== 1) return undefined;
  const level = Number(user.member_level);
  return Number.isInteger(level) && level >= 0 && level <= 3 ? level : undefined;
}

function getProductPriceList(
  product: BestsellerProduct,
  key: keyof BestsellerProduct,
): BestsellerPriceInfo[] {
  const value = product[key];
  return Array.isArray(value) ? (value as BestsellerPriceInfo[]) : [];
}

function findPriceInfo(
  product: BestsellerProduct,
  currencyName: string,
  surface: BestsellerDisplaySurface,
  userInfo: unknown,
): BestsellerPriceInfo | undefined {
  const suffix = surface === 'pc' ? 'Pc' : 'Wap';
  const fallbackSuffix = surface === 'pc' ? 'Wap' : 'Pc';
  const level = getUserMemberLevel(userInfo);

  if (level !== undefined && product[`discountPercentV${level}` as keyof BestsellerProduct]) {
    const memberCandidates = [
      `priceInfo${suffix}V${level}`,
      `priceInfoV${level}`,
      `priceInfo${fallbackSuffix}V${level}`,
    ] as Array<keyof BestsellerProduct>;
    const memberPrice = memberCandidates
      .flatMap((key) => getProductPriceList(product, key))
      .find((item) => item.currency === currencyName);
    if (memberPrice) return memberPrice;
  }

  return (
    [`priceInfo${suffix}`, 'priceInfo', `priceInfo${fallbackSuffix}`] as Array<
      keyof BestsellerProduct
    >
  )
    .flatMap((key) => getProductPriceList(product, key))
    .find((item) => item.currency === currencyName);
}

function getUsdPriceInfo(product: BestsellerProduct): BestsellerPriceInfo | undefined {
  return (['priceInfo', 'priceInfoPc', 'priceInfoWap'] as Array<keyof BestsellerProduct>)
    .flatMap((key) => getProductPriceList(product, key))
    .find((item) => item.currency === 'USD');
}

function convertUsdPrice(value: string, currency: Currency): string {
  const numericValue = numberText(value);
  const exchangeRate = Number(priceText(currency?.exchange_rate));
  if (!numericValue || !Number.isFinite(exchangeRate) || exchangeRate <= 0) return '';
  return (Number(numericValue) * exchangeRate).toFixed(2);
}

function fallbackPriceText(
  productValue: string,
  formattedValue: string,
  currency: Currency,
): string {
  const numericValue = numberText(productValue);
  return numericValue ? formatPriceNumberWithSymbol(numericValue, currency) : formattedValue;
}

export function resolveBestsellerDisplayPrice(
  product: BestsellerProduct,
  userInfo: unknown,
  currency: Currency,
  surface: BestsellerDisplaySurface = 'pc',
): BestsellerDisplayPrice {
  const currencyName = currency?.name;
  const displayCurrency = resolveBestsellerDisplayCurrency(currency);
  const currentPrice = currencyName
    ? findPriceInfo(product, currencyName, surface, userInfo)
    : undefined;
  const sellPrice = priceText(currentPrice?.sell_price);

  if (sellPrice) {
    const originalPrice = priceText(currentPrice?.price);

    return {
      price: formatPriceNumberWithSymbol(sellPrice, displayCurrency),
      originPrice:
        originalPrice && originalPrice !== sellPrice
          ? formatPriceNumberWithSymbol(originalPrice, displayCurrency)
          : undefined,
    };
  }

  const usdPrice = currencyName && currencyName !== 'USD' ? getUsdPriceInfo(product) : undefined;
  const convertedSellPrice = usdPrice ? convertUsdPrice(usdPrice.sell_price, currency) : '';
  const convertedOriginPrice = usdPrice ? convertUsdPrice(usdPrice.price, currency) : '';

  if (convertedSellPrice) {
    return {
      price: formatPriceNumberWithSymbol(convertedSellPrice, displayCurrency),
      originPrice:
        convertedOriginPrice && convertedOriginPrice !== convertedSellPrice
          ? formatPriceNumberWithSymbol(convertedOriginPrice, displayCurrency)
          : undefined,
    };
  }

  return {
    price: fallbackPriceText(product.priceValue, product.price, displayCurrency),
    originPrice:
      product.originPriceValue && product.originPriceValue !== product.priceValue
        ? fallbackPriceText(product.originPriceValue, product.originPrice, displayCurrency)
        : undefined,
  };
}

export function useBestsellerDisplayPrice(
  product: BestsellerProduct,
  surface: BestsellerDisplaySurface = 'pc',
): BestsellerDisplayPrice {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  return useMemo(
    () => resolveBestsellerDisplayPrice(product, userInfo, currency, surface),
    [currency, product, surface, userInfo],
  );
}
